import { createReducer, on } from '@ngrx/store';
import { SortData } from 'src/app/models/common/sort-data.model';
import { DiaryNotesFilter } from 'src/app/models/diary-notes/diary-notes-filter.model';
import { GeneralError, SortDirection } from 'vidis-common';
import { AvailableFilters, DiaryNoteItem, DiaryNotePagination } from '../../models/diary-notes/diary-note.model';
import * as DiaryNotesActions from '../actions/diary-notes.action';

export interface State {
  diaryNotes: DiaryNoteItem[],
  diaryNotesErrors: GeneralError[],
  availableFilters: AvailableFilters,
  activeFilters: DiaryNotesFilter,
  sortData: SortData,
  pagination: DiaryNotePagination,
  loading: boolean;
  totalItems: number,
  isLoaded: boolean;
}

const initialState: State = {
  diaryNotes: undefined,
  diaryNotesErrors: undefined,
  availableFilters: undefined,
  activeFilters: {dateRange: {from: '', until: ''}},
  sortData: {sort: 'date', order: SortDirection.descending},
  pagination: {
    page: 0,
    totalPages: undefined,
    totalFiltredItems: undefined,
    pageSize: 10,
  },
  totalItems: undefined,
  loading: undefined,
  isLoaded: false
};

export const diaryNotesReducer = createReducer(
  initialState,
  on(DiaryNotesActions.fetchDiaryNotesAction, DiaryNotesActions.fetchDiaryNotesCountAction, state => ({
    ...state,
    loading: true
  })),
  on(DiaryNotesActions.setDiaryNotesCountAction, (state, {countResult}): State => ({
    ...state,
    totalItems: countResult?.count !== undefined ? countResult.count : initialState.totalItems,
    loading: false
  })),
  on(DiaryNotesActions.incrementDiaryNotesCountAction, state => ({
    ...state,
    totalItems: state.totalItems + 1
  })),
  on(DiaryNotesActions.setDiaryNotesAction, (state, {
    diaryNotes,
    availableFilters,
    page,
    totalItems,
    totalPages
  }) => ({
    ...state,
    diaryNotes,
    availableFilters: {
      ...state.availableFilters,
      ...availableFilters
    },
    pagination: {
      ...state.pagination,
      page: page,
      totalPages: totalPages,
      totalFiltredItems: totalItems
    },
    loading: false,
    isLoaded: true
  })),
  on(DiaryNotesActions.setDiaryNotesPageSizeAction, (state, {pageSize}) => ({
    ...state,
    pagination: {
      ...state.pagination,
      pageSize: pageSize
    }
  })),
  on(DiaryNotesActions.setDiaryNotesSortDataAction, (state, {sortData}) => ({
    ...state,
    sortData
  })),
  on(DiaryNotesActions.setDiaryNotesActiveFiltersAction, (state, {activeFilters}) => ({
    ...state,
    activeFilters
  })),
  on(DiaryNotesActions.removeDiaryNotesOneActiveFilterAction, (state, {key}) => {
    switch (key) {
      case 'date':
        return {
          ...state,
          activeFilters: {
            ...state.activeFilters,
            dateRange: {from: '', until: ''}
          }
        };
      default:
        return {
          ...state,
          activeFilters: {
            ...state.activeFilters,
            [key]: undefined
          }
        };
    }
  }),
  on(DiaryNotesActions.removeDiaryNotesAllActiveFiltersAction, state => ({
    ...state,
    activeFilters: {dateRange: {from: '', until: ''}}
  })),
  on(DiaryNotesActions.postDiaryNoteAction, state => ({
    ...state
  })),
  on(DiaryNotesActions.setDiaryNotesErrorsAction, (state, {diaryNotesErrors}) => ({
    ...state,
    diaryNotesErrors,
    loading: false
  })),
  on(DiaryNotesActions.clearDiaryNotesErrorAction, state => ({
    ...state,
    diaryNotesError: undefined
  })),
  on(DiaryNotesActions.diaryNoteDeleteSucceededAction, (state, {id, newNote, status}) => {
    if (status === 204) {
      return {
        ...state,
        diaryNotes: state.diaryNotes.filter(dn => dn.id !== id)
      };
    }
    return {
      ...state,
      diaryNotes: state.diaryNotes.map(dn => dn.id === newNote.note.id ? newNote.note : dn)
    };
  }),
  on(DiaryNotesActions.clearDiaryNotesAction, () => (initialState))
);
